import { Constants } from '@pwc/pwc-deals-web-common';
import get from 'lodash/get';
import { ENTITY_TYPES } from '../constants/General';
import featureFlags from '../helpers/featureFlags';

const STRUCTURED_ANALYSIS_PATH = 'template-analyses';
const ANALYSIS_PATH = 'analyses';
const KFA_PATH = 'key-focus-areas';
const CONTENT_HUB_PATH = 'content-hub';

const endSlash = new RegExp(/\/$/);
const startSlash = new RegExp(/^\//);

const assembleUrl = (base) => (path) => {
  const cleanBase = base.replace(endSlash, '');
  const cleanPath = path.replace(startSlash, '');

  return `${cleanBase}/${cleanPath}`;
};

export const isStructuredAnalysis2ContentType = (contentType) =>
  contentType && contentType === Constants.ContentTypes.StructuredAnalysis;

export const structuredRoutes = {
  [Constants.QUALITY_OF_EARNINGS]: 'quality-of-earnings',
  [Constants.NET_WORKING_CAPITAL]: 'net-working-capital',
  [Constants.DEBT_AND_DEBT_LIKE]: 'debt-debt-like',
};

export const structuredRoutesForSubType = {
  [Constants.structuredDocumentSubtypeCodes[Constants.QUALITY_OF_EARNINGS]]:
    'quality-of-earnings',
  [Constants.structuredDocumentSubtypeCodes[Constants.NET_WORKING_CAPITAL]]:
    'net-working-capital',
  [Constants.structuredDocumentSubtypeCodes[Constants.DEBT_AND_DEBT_LIKE]]:
    'debt-debt-like',
};

export const buildUrl = (base, path) => assembleUrl(base)(path);

export const projectOverviewPath = (projectId, isPwcUser) =>
  !featureFlags.isPOTopicRefactorEnabled
    ? `/projects/${projectId}`
    : `/projects/${projectId}/topics/${isPwcUser ? 'draft' : 'shared'}`;

export const contentHubPath = (projectId) =>
  `/projects/${projectId}/${CONTENT_HUB_PATH}`;

export const hypothesesListPath = (projectId) =>
  `/projects/${projectId}/${KFA_PATH}`;

export const viewHypothesisPath = (projectId, hypothesisId) =>
  `/projects/${projectId}/${KFA_PATH}/${hypothesisId}/view`;

export const createHypothesisPath = (projectId) =>
  `/projects/${projectId}/${KFA_PATH}/create`;

export const editHypothesisPath = (projectId, hypothesisId) =>
  `/projects/${projectId}/${KFA_PATH}/${hypothesisId}/edit`;

export const insightsListPath = (projectId) =>
  `/projects/${projectId}/insights`;

export const viewInsightPath = (projectId, insightId) =>
  `/projects/${projectId}/insights/${insightId}/view`;

export const createInsightPath = (projectId) =>
  `/projects/${projectId}/insights/create`;

export const editInsightPath = (projectId, insightId) =>
  `/projects/${projectId}/insights/${insightId}/edit`;

export const analysesListPath = (projectId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}`;

export const analysesGroupsListPath = (projectId) =>
  `/projects/${projectId}/analyses-groups/custom`;

export const addAnalysisTypePath = (projectId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/add-type`;

export const addContentSetTypePath = (projectId) =>
  `/projects/${projectId}/content-set`;

export const createCustomAnalysisPath = (projectId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/create`;

export const viewAnalysisPath = (projectId, analysisId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${analysisId}/view`;

export const editAnalysisPath = (projectId, analysisId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${analysisId}/edit`;

export const viewAnalysisExcelTablePath = (projectId, analysisId, version) =>
  `/projects/${projectId}/content/${analysisId}/${version}/exportfulltable`;

export const reportPageCountPath = (projectId, territory) =>
  `/projects/${projectId}/preview/content/${territory}`;

export const reportTilePath = (projectId, analysisId, version) =>
  `/projects/${projectId}/preview/tile/${analysisId}/${version}`;

export const reportPreviewPath = (
  projectId,
  entityId,
  version,
  isUnStructured = true,
  entityType = ENTITY_TYPES.ANALYSIS
) =>
  `/projects/${projectId}/${
    entityType === ENTITY_TYPES.ANALYSIS ? ANALYSIS_PATH : KFA_PATH
  }/${entityId}/report-preview/${entityType}/${version}?isUnStructured=${isUnStructured}`;

export const reportTopicsPreviewPath = (
  projectId,
  contentThreadId,
  versionStatus,
  isPwcUser
) => {
  const versions = {
    [Constants.VERSION_STATUS.SHARED.value]: 'shared',
  };

  const version = get(versions, versionStatus, 'latest');

  const baseProjectOverviewPath = projectOverviewPath(projectId, isPwcUser);
  const path = [
    'topics',
    contentThreadId,
    'report-preview',
    versionStatus,
    version,
  ]
    .filter(Boolean)
    .join('/');
  return `${baseProjectOverviewPath}/${path}`;
};

export const viewQualityOfEarningsPath = (projectId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${
    structuredRoutes[Constants.QUALITY_OF_EARNINGS]
  }`;

export const viewNetWorkingCapitalPath = (projectId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${
    structuredRoutes[Constants.NET_WORKING_CAPITAL]
  }`;

export const viewDebtAndDebtLikePath = (projectId) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${
    structuredRoutes[Constants.DEBT_AND_DEBT_LIKE]
  }`;

export const editStructuredAnalysisDocument = (projectId, documentType) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${structuredRoutes[documentType]}/edit`;

export const viewStructuredAnalysisPathForDocument = (
  projectId,
  documentCode
) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${structuredRoutes[documentCode]}`;

export const addStructuredAnalysisPathForDocument = (projectId, route) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${route}/edit`;

export const viewStructuredAnalysisPathForSubType = (projectId, subType) =>
  `/projects/${projectId}/${ANALYSIS_PATH}/${structuredRoutesForSubType[subType]}`;

export const viewStructuredAnalysis2PathForSubType = ({
  projectId,
  contentThreadId,
  isPwcUser,
}) => {
  return `/projects/${projectId}/${STRUCTURED_ANALYSIS_PATH}/${contentThreadId}/view/${
    isPwcUser ? 'draft' : 'shared'
  }`;
};

export const editStructuredAnalysis2PathForSubType = ({
  projectId,
  contentThreadId,
}) => {
  return `/projects/${projectId}/${STRUCTURED_ANALYSIS_PATH}/${contentThreadId}/edit`;
};

export const reportsListPath = (projectId) => `/projects/${projectId}/reports`;

export const legacyFilesPath = (projectId) => `/projects/${projectId}/files`;

export const filesPath = (projectId) => `/projects/${projectId}/files/folders`;

export const createReportPath = (projectId) =>
  `/projects/${projectId}/reports/create-report`;

export const getUserManagementPath = (projectId) =>
  `/projects/${projectId}/team-members`;

export const projectSettingsPath = (projectId) =>
  `/projects/${projectId}/settings`;

export const structuredAnalysisDocumentsPath = (projectId) =>
  `/projects/${projectId}/StructuredAnalysisDocuments`;

export const getDocumentsWithDataPath = (projectId) =>
  `/projects/${projectId}/StructuredAnalysisDocuments/withData`;

export const createProjectPagePath = '/projects/create-project';

export const projectDisclaimerPath = (projectId) =>
  `/projects/${projectId}/project-disclaimer`;

export const fetchDivestitureDiagnosticResultsPath = (projectId) =>
  `/projects/${projectId}/diagnostics`;

export const resetDivestitureDiagnosticResultsPath = (projectId) =>
  `/projects/${projectId}/diagnostics/reset`;

export const getDisclaimerPath = (projectId) =>
  `/projects/${projectId}/project-disclaimer`;

export const saveCategoriesPath = (projectId) =>
  `/projects/${projectId}/diagnostics/batch`;

export const saveOverallComplexityPath = (projectId) =>
  `/projects/${projectId}/diagnostics/overallComplexity`;

export const getAllLatestDocumentsWithContent = (
  projectId,
  isViewingAsClient
) =>
  `/projects/${projectId}/StructuredAnalysisDocuments/getLatestContent?isViewingAsClient=${isViewingAsClient}`;

export const createProjectTopicPath = (projectId) =>
  `/projects/${projectId}/topic/create`;
